/**
 * 할인/감면 신청 모달 Vue.js 통합 스크립트
 * 기존 JB 모달 패턴과 Vue.js 컴포넌트를 연동
 */

import Vue from 'vue';
import RebateApplicationModal from './RebateApplicationModal.vue';

// 전역 Vue 앱 인스턴스 저장
let rebateModalApp = null;

/**
 * 모달 DOM 엘리먼트 생성
 *
 * @returns {HTMLElement} 생성된 모달 엘리먼트
 */
function createModalElement() {
    const modalElement = document.createElement('div');
    modalElement.id = 'rebateApplicationModal';
    modalElement.className = 'rebate-application-modal-wrapper';
    modalElement.style.display = 'none';
    modalElement.setAttribute('aria-hidden', 'true');
    modalElement.setAttribute('aria-modal', 'true');
    modalElement.setAttribute('role', 'dialog');
    modalElement.setAttribute('aria-labelledby', 'rebate-modal-title');

    return modalElement;
}

/**
 * 인증 파라미터를 API에서 가져오는 함수
 */
async function fetchAuthParams() {
    try {
        const response = await fetch('/api/mdds/auth-params', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            }
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('[ERROR] fetchAuthParams 실패:', error);
        return {
            success: false,
            error: error.message || '인증 파라미터 API 호출 실패'
        };
    }
}

/**
 * 할인/감면 신청 모달 표시 함수
 * 기존 JavaScript에서 호출하는 진입점
 *
 * @param {number|string} courseNo - 강좌 번호 (CR_NO)
 * @param {function} completedCallback - 신청 완료 콜백 함수
 */
async function showRebateApplicationModal(courseNo, completedCallback = null) {
    // 로그인 상태 확인
    if (!window.loginMember) {
        alert('로그인 후 이용 가능합니다.');
        return;
    }

    // 강좌 번호 유효성 검사
    if (!courseNo) {
        alert('강좌 정보를 확인할 수 없습니다.');
        return;
    }

    try {
        // 기존 모달이 열려있다면 정리
        if (rebateModalApp) {
            rebateModalApp.$destroy();
            rebateModalApp = null;
        }

        // 모달 엘리먼트 생성 또는 가져오기
        let modalElement = document.getElementById('rebateApplicationModal');
        if (!modalElement) {
            modalElement = createModalElement();
            document.body.appendChild(modalElement);
        }

        // API를 통해 인증 파라미터 획득
        const authParams = await fetchAuthParams();
        
        if (!authParams.success) {
            alert('인증 파라미터를 가져오는 중 오류가 발생했습니다: ' + (authParams.error || '알 수 없는 오류'));
            return;
        }

        // Vue 2.x 인스턴스 생성 및 마운트
        rebateModalApp = new Vue({
            el: '#rebateApplicationModal',
            components: {
                'rebate-application-modal': RebateApplicationModal,
            },
            data: {
                courseNo,
                modalId: 'rebateApplicationModal',
                authParams,
            },
            methods: {
                handleApplicationCompleted(result) {
                    if (typeof completedCallback === 'function') {
                        completedCallback(result);
                    }
                }
            },
            template: '<rebate-application-modal :course-no="courseNo" :modal-id="modalId" :auth-params="authParams" @application-completed="handleApplicationCompleted"></rebate-application-modal>',
        });

        // 모달 표시
        modalElement.style.display = 'flex';
        document.body.classList.add('modal-open');
        modalElement.setAttribute('aria-hidden', 'false');

        // 접근성: Vue 렌더링 완료 후 모달 제목으로 포커스 이동
        // Vue 마운트 후 원본 modalElement는 DOM에서 교체됨 → rebateModalApp.$el 사용
        rebateModalApp.$nextTick(() => {
            const rootEl = rebateModalApp.$el;
            const titleEl = rootEl.querySelector('h2.title[tabindex="-1"]');
            const firstFocusable = titleEl || rootEl.querySelector('button:not([disabled])');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        });
    } catch (error) {
        console.error('Rebate modal error:', error);
        alert('모달을 열 수 없습니다. 페이지를 새로고침 후 다시 시도해 주세요.');
    }
}

/**
 * 할인/감면 신청 모달 닫기 함수
 *
 * @param {Event} event - 이벤트 객체 (선택사항)
 */
function closeRebateApplicationModal(event) {
    if (event) {
        event.preventDefault();
    }

    const modalElement = document.getElementById('rebateApplicationModal');

    if (modalElement) {
        // 모달 숨기기
        modalElement.style.display = 'none';
        modalElement.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
    }

    // Vue 인스턴스 제거
    if (rebateModalApp) {
        rebateModalApp.$destroy();
        rebateModalApp = null;
    }
}

/**
 * 기존 openTargetModal 함수와의 통합
 *
 * @param {Event} event - 클릭 이벤트
 * @param {string} modalType - 모달 타입 ("rebateApplicationModal")
 * @param {Object} options - 추가 옵션
 */
function openRebateTargetModal(event, modalType, options = {}) {
    if (modalType === 'rebateApplicationModal') {
        const courseNo = options.courseNo || event.target.dataset.courseNo ||
            event.target.getAttribute('data-course-no');

        if (!courseNo) {
            alert('강좌 정보를 불러올 수 없습니다.');
            return;
        }

        showRebateApplicationModal(courseNo, options.completedCallback);
    }
}

/**
 * JSP 페이지의 할인/감면 신청 버튼 클릭 핸들러
 * HTML: <button onclick="handleRebateApplicationClick(this)" data-course-no="123">할인/감면 신청</button>
 *
 * @param {HTMLElement} element - 클릭된 버튼 엘리먼트
 */
async function handleRebateApplicationClick(element) {
    const courseNo = element.dataset.courseNo || element.getAttribute('data-course-no');

    if (!courseNo) {
        alert('강좌 정보를 확인할 수 없습니다.');
        return;
    }

    // 로그인 상태 확인 (필요시)
    if (typeof window.checkLoginStatus === 'function' && !window.checkLoginStatus()) {
        alert('로그인이 필요한 서비스입니다.');
        // 로그인 페이지로 리다이렉트 또는 로그인 모달 표시
        return;
    }

    // 할인/감면 신청 모달 표시
    await showRebateApplicationModal(courseNo);
}

/**
 * mypage-education-rebateList.jsp에서 사용할 할인/감면 신청 버튼 핸들러
 * 
 * @param {string} courseNo - 강좌 번호
 */
async function openRebateModal(courseNo) {
    if (!courseNo) {
        alert('강좌 정보를 확인할 수 없습니다.');
        return;
    }

    await showRebateApplicationModal(courseNo);
}

// 전역 함수로 등록
window.showRebateApplicationModal = showRebateApplicationModal;
window.closeRebateApplicationModal = closeRebateApplicationModal;
window.openRebateTargetModal = openRebateTargetModal;
window.handleRebateApplicationClick = handleRebateApplicationClick;
window.openRebateModal = openRebateModal; // JSP에서 직접 호출용

/**
 * 키보드 이벤트 처리 (ESC 키로 모달 닫기)
 */
document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
        const modalElement = document.getElementById('rebateApplicationModal');
        if (modalElement && modalElement.style.display !== 'none') {
            closeRebateApplicationModal(event);
        }
    }
});

/**
 * 모듈 초기화 및 상태 확인
 */
document.addEventListener('DOMContentLoaded', () => {
    // Vue.js 사용 가능 여부 확인
    if (typeof Vue === 'undefined') {
        console.warn('Vue.js가 로드되지 않았습니다. 할인/감면 신청 모달이 작동하지 않을 수 있습니다.');
    }

    // 기존 할인/감면 신청 버튼들에 자동으로 이벤트 핸들러 연결 (선택사항)
    const rebateButtons = document.querySelectorAll('[data-action="rebate-application"]');
    rebateButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            handleRebateApplicationClick(event.target);
        });
    });
});

// CommonJS/AMD 호환성
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        showRebateApplicationModal,
        closeRebateApplicationModal,
        openRebateTargetModal,
        handleRebateApplicationClick,
        openRebateModal,
    };
}


// WEBPACK FOOTER //
// ./src/main/education/RebateApplicationModalIntegration.js